import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Skeleton,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  FormatQuote as QuoteIcon,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Pending as PendingIcon,
  MenuBook as DictionaryIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useSnackbar } from 'notistack';

import { dashboardService, ephemerisService } from '../../services/api';
import AdminResend from '../WhatsApp/AdminResend';

const StatCard = ({ title, value, icon, color, subtitle, loading }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={40} />
          ) : (
            <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentSubscriptions, setRecentSubscriptions] = useState([]);
  const [proverbUsage, setProverbUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayEphemeris, setTodayEphemeris] = useState(null);
  
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, subscriptionsRes, usageRes, todayRes] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentSubscriptions(),
        dashboardService.getProverbUsage(),
        ephemerisService.getToday().catch(() => null),
      ]);

      setStats(statsRes.data.data);
      setRecentSubscriptions(subscriptionsRes.data.data);
      setProverbUsage(usageRes.data.data);
      setTodayEphemeris(todayRes?.data?.data);
    } catch (error) {
      enqueueSnackbar('Erreur lors du chargement des données', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'blocked': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'pending': return 'En attente';
      case 'blocked': return 'Bloqué';
      default: return status;
    }
  };

  // Préparer les données pour le graphique
  const chartData = proverbUsage ? proverbUsage.labels.map((label, index) => ({
    name: label,
    proverbes: proverbUsage.months[index],
  })) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Tableau de bord
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ephemerides')}
        >
          Générer éphéméride
        </Button>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Abonnés actifs"
            value={stats?.subscribers?.active || 0}
            icon={<PeopleIcon />}
            color="primary"
            subtitle={`${stats?.subscribers?.pending || 0} en attente`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Proverbes"
            value={stats?.proverbs?.total || 0}
            icon={<QuoteIcon />}
            color="secondary"
            subtitle={`${stats?.proverbs?.remaining || 0} restants cette année`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Éphémérides générées"
            value={stats?.ephemerides?.generatedThisYear || 0}
            icon={<CalendarIcon />}
            color="success"
            subtitle={`${stats?.ephemerides?.progress || 0}% de l'année`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages aujourd'hui"
            value={stats?.messages?.sentToday || 0}
            icon={<WhatsAppIcon />}
            color="info"
            subtitle={`${stats?.messages?.sentThisMonth || 0} ce mois`}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Dictionnaire : avancement de la relecture */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DictionaryIcon color="primary" />
            Dictionnaire : mots vérifiés
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={80} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h3" component="span" sx={{ fontWeight: 800 }}>{stats?.dictionary?.verified ?? 0}</Typography>
                <Typography variant="h6" component="span" color="text.secondary">
                  / {stats?.dictionary?.total ?? 0} mots ({stats?.dictionary?.percentage ?? 0} %)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {stats?.dictionary?.remaining ?? 0} restent à vérifier
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={stats?.dictionary?.percentage ?? 0} sx={{ height: 10, borderRadius: 5, mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(stats?.dictionary?.byLetter || []).map((l) => (
                  <Chip
                    key={l.letter}
                    size="small"
                    variant={l.verified === l.total ? 'filled' : 'outlined'}
                    color={l.verified === l.total ? 'success' : l.verified > 0 ? 'warning' : 'default'}
                    label={`${l.letter} ${l.verified}/${l.total}`}
                  />
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Éphéméride du jour */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                Éphéméride du jour
              </Typography>
              
              {todayEphemeris ? (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                    {new Date(todayEphemeris.date).getDate()}
                  </Typography>
                  <Typography variant="h6">
                    {todayEphemeris.weekday?.duala_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {todayEphemeris.weekday?.french_name}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {todayEphemeris.month?.duala_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {todayEphemeris.month?.french_name}
                  </Typography>
                  
                  <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      MUNIA
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                      "{todayEphemeris.proverb?.duala_text}"
                    </Typography>
                  </Paper>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/ephemerides/preview/${todayEphemeris.date}`)}
                  >
                    Voir l'aperçu
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    Aucune éphéméride générée aujourd'hui
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/ephemerides')}
                  >
                    Générer maintenant
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Inscriptions récentes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="primary" />
                  Inscriptions récentes
                </Typography>
                {stats?.subscribers?.pending > 0 && (
                  <Chip 
                    label={`${stats.subscribers.pending} en attente`} 
                    color="warning" 
                    size="small"
                    icon={<PendingIcon />}
                  />
                )}
              </Box>
              
              <List dense>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <ListItem key={i}>
                      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                      <Skeleton width="80%" />
                    </ListItem>
                  ))
                ) : recentSubscriptions.length > 0 ? (
                  recentSubscriptions.slice(0, 5).map((sub) => (
                    <ListItem 
                      key={sub.id}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                      onClick={() => navigate(`/subscribers/${sub.id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {sub.first_name?.[0] || sub.phone_number[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={sub.first_name ? `${sub.first_name} ${sub.last_name || ''}` : sub.phone_number}
                        secondary={new Date(sub.subscription_date).toLocaleDateString('fr-FR')}
                      />
                      <Chip 
                        label={getStatusLabel(sub.status)} 
                        color={getStatusColor(sub.status)} 
                        size="small" 
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Aucune inscription récente
                  </Typography>
                )}
              </List>
              
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/subscribers')}
                sx={{ mt: 1 }}
              >
                Voir tous les abonnés
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Graphique utilisation des proverbes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="primary" />
                Utilisation des proverbes {stats?.currentYear}
              </Typography>
              
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="proverbes" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Utilisés: {stats?.proverbs?.usedThisYear || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Restants: {stats?.proverbs?.remaining || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
